import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Form, Button, ListGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";
import { getEjecutivos, getMedios, getEmpresasClientes, getEstadosAviso } from '../services/apiService';

const Filtros = ({ onFilter, fechaInicioDefault, fechaFinDefault }) => {
  // Estados para los filtros
  const [fechaInicio, setFechaInicio] = useState(fechaInicioDefault || new Date());
  const [fechaFin, setFechaFin] = useState(fechaFinDefault || new Date());
  const [ejecutivoId, setEjecutivoId] = useState('');
  const [ejecutivoNombre, setEjecutivoNombre] = useState('');
  const [ejecutivosFiltrados, setEjecutivosFiltrados] = useState([]);
  const [mostrarSugerenciasEjecutivo, setMostrarSugerenciasEjecutivo] = useState(false);
  
  const [medioId, setMedioId] = useState('58'); // Predeterminado ID 58 (ANTONIO PUGA Y CIA LTDA)
  const [medioNombre, setMedioNombre] = useState('ANTONIO PUGA Y CIA LTDA');
  const [mediosFiltrados, setMediosFiltrados] = useState([]);
  const [mostrarSugerenciasMedio, setMostrarSugerenciasMedio] = useState(false);
  
  const [empresaClienteId, setEmpresaClienteId] = useState('');
  const [empresaClienteNombre, setEmpresaClienteNombre] = useState('');
  const [estadoAvisoId, setEstadoAvisoId] = useState('');
  
  // Estados para las opciones de los selectores
  const [ejecutivos, setEjecutivos] = useState([]);
  const [medios, setMedios] = useState([]);
  const [empresasClientes, setEmpresasClientes] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const [estadosAviso, setEstadosAviso] = useState([]);
  const [filtrosInicialesEnviados, setFiltrosInicialesEnviados] = useState(false);
  
  // Referencias para cerrar los autocompletados cuando se hace clic fuera
  const empresaClienteRef = useRef(null);
  const ejecutivoRef = useRef(null);
  const medioRef = useRef(null);
  
  // Efecto para cerrar los desplegables cuando se hace clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (empresaClienteRef.current && !empresaClienteRef.current.contains(event.target)) {
        setMostrarSugerencias(false);
      }
      if (ejecutivoRef.current && !ejecutivoRef.current.contains(event.target)) {
        setMostrarSugerenciasEjecutivo(false);
      }
      if (medioRef.current && !medioRef.current.contains(event.target)) {
        setMostrarSugerenciasMedio(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [empresaClienteRef, ejecutivoRef, medioRef]);
  
  // Cargar datos para los filtros
  useEffect(() => {
    const cargarDatosFiltros = async () => {
      try {
        // Cargar ejecutivos
        const ejecutivosResponse = await getEjecutivos();
        if (ejecutivosResponse.success) {
          // Ordenar ejecutivos alfabéticamente
          const ejecutivosOrdenados = ejecutivosResponse.data.sort((a, b) => 
            a.nombre.localeCompare(b.nombre)
          );
          setEjecutivos(ejecutivosOrdenados);
          setEjecutivosFiltrados(ejecutivosOrdenados);
        }
        
        // Cargar medios
        const mediosResponse = await getMedios();
        if (mediosResponse.success) {
          // Ordenar medios alfabéticamente
          const mediosOrdenados = mediosResponse.data.sort((a, b) =>
            a.nombre.localeCompare(b.nombre)
          );
          setMedios(mediosOrdenados);
          setMediosFiltrados(mediosOrdenados);
        }
        
        // Cargar empresas clientes iniciales (sin filtro)
        const empresasClientesResponse = await getEmpresasClientes();
        if (empresasClientesResponse.success) {
          setEmpresasClientes(empresasClientesResponse.data);
        }

        // Cargar estados de aviso
        const estadosAvisoResponse = await getEstadosAviso();
        if (estadosAvisoResponse.success) {
          setEstadosAviso(estadosAvisoResponse.data);
        }
      } catch (error) {
        console.error('Error al cargar datos para los filtros:', error);
      }
    };
    
    cargarDatosFiltros();
  }, []);

  // Filtrar ejecutivos al escribir
  const filtrarEjecutivos = async (texto) => {
    setEjecutivoNombre(texto);
    
    try {
      if (texto.length === 0) {
        setEjecutivoId('');
        // Cargar todos los ejecutivos nuevamente
        const ejecutivosResponse = await getEjecutivos();
        if (ejecutivosResponse.success) {
          const ejecutivosOrdenados = ejecutivosResponse.data.sort((a, b) => 
            a.nombre.localeCompare(b.nombre)
          );
          setEjecutivos(ejecutivosOrdenados);
          setEjecutivosFiltrados(ejecutivosOrdenados);
        }
        setMostrarSugerenciasEjecutivo(false);
      } else if (texto.length >= 2) {
        // Buscar en el servidor si hay más de 2 caracteres
        const response = await getEjecutivos({ q: texto });
        if (response.success) {
          const ejecutivosOrdenados = response.data.sort((a, b) => 
            a.nombre.localeCompare(b.nombre)
          );
          setEjecutivosFiltrados(ejecutivosOrdenados);
          setMostrarSugerenciasEjecutivo(true);
        }
      } else {
        // Para 1 caracter, filtra localmente
        const filtrados = ejecutivos.filter(
          ejecutivo => ejecutivo.nombre.toLowerCase().includes(texto.toLowerCase())
        );
        setEjecutivosFiltrados(filtrados);
        setMostrarSugerenciasEjecutivo(true);
      }
    } catch (error) {
      console.error('Error al filtrar ejecutivos:', error);
      // Fallback a filtrado local en caso de error
      const filtrados = ejecutivos.filter(
        ejecutivo => ejecutivo.nombre.toLowerCase().includes(texto.toLowerCase())
      );
      setEjecutivosFiltrados(filtrados);
      setMostrarSugerenciasEjecutivo(true);
    }
  };

  // Seleccionar un ejecutivo de la lista
  const seleccionarEjecutivo = (id, nombre) => {
    setEjecutivoId(id);
    setEjecutivoNombre(nombre);
    setMostrarSugerenciasEjecutivo(false);
  };

  // Filtrar medios al escribir
  const filtrarMedios = async (texto) => {
    setMedioNombre(texto);
    
    try {
      if (texto.length === 0) {
        setMedioId('');
        // Cargar todos los medios nuevamente
        const mediosResponse = await getMedios();
        if (mediosResponse.success) {
          const mediosOrdenados = mediosResponse.data.sort((a, b) => 
            a.nombre.localeCompare(b.nombre)
          );
          setMedios(mediosOrdenados);
          setMediosFiltrados(mediosOrdenados);
        }
        setMostrarSugerenciasMedio(false);
      } else if (texto.length >= 2) {
        // Buscar en el servidor si hay más de 2 caracteres
        const response = await getMedios({ q: texto });
        if (response.success) {
          const mediosOrdenados = response.data.sort((a, b) => 
            a.nombre.localeCompare(b.nombre)
          );
          setMediosFiltrados(mediosOrdenados);
          setMostrarSugerenciasMedio(true);
        }
      } else {
        // Para 1 caracter, filtra localmente
        const filtrados = medios.filter(
          medio => medio.nombre.toLowerCase().includes(texto.toLowerCase())
        );
        setMediosFiltrados(filtrados);
        setMostrarSugerenciasMedio(true);
      }
    } catch (error) {
      console.error('Error al filtrar medios:', error);
      // Fallback a filtrado local en caso de error
      const filtrados = medios.filter(
        medio => medio.nombre.toLowerCase().includes(texto.toLowerCase())
      );
      setMediosFiltrados(filtrados);
      setMostrarSugerenciasMedio(true);
    }
  };

  // Seleccionar un medio de la lista
  const seleccionarMedio = (id, nombre) => {
    setMedioId(id);
    setMedioNombre(nombre);
    setMostrarSugerenciasMedio(false);
  };

  // Buscar empresas clientes cuando se escribe
  const buscarEmpresasClientes = async (texto) => {
    try {
      setEmpresaClienteNombre(texto);
      if (texto.length >= 3) {
        const response = await getEmpresasClientes({ q: texto });
        if (response.success) {
          setEmpresasClientes(response.data);
          setMostrarSugerencias(true);
        }
      } else if (texto.length === 0) {
        // Si el campo está vacío, limpiar el ID seleccionado
        setEmpresaClienteId('');
        // Cargar lista inicial nuevamente
        const response = await getEmpresasClientes();
        if (response.success) {
          setEmpresasClientes(response.data);
        }
        setMostrarSugerencias(false);
      }
    } catch (error) {
      console.error('Error al buscar empresas clientes:', error);
    }
  };

  // Seleccionar una empresa cliente de la lista
  const seleccionarEmpresaCliente = (id, nombre) => {
    setEmpresaClienteId(id);
    setEmpresaClienteNombre(nombre);
    setMostrarSugerencias(false);
  };

  // Aplicar filtros automáticamente al iniciar, sólo una vez
  useEffect(() => {
    // Sólo enviar los filtros iniciales una vez
    if (!filtrosInicialesEnviados) {
      const filtrosIniciales = {
        fechaInicio: format(fechaInicio, 'yyyy-MM-dd'),
        fechaFin: format(fechaFin, 'yyyy-MM-dd'),
        medio: medioId // Incluir medio predeterminado (ANTONIO PUGA Y CIA LTDA)
      };
      
      console.log('Enviando filtros iniciales una sola vez:', filtrosIniciales);
      onFilter(filtrosIniciales);
      setFiltrosInicialesEnviados(true);
    }
  }, [fechaInicio, fechaFin, medioId, onFilter, filtrosInicialesEnviados]);
  
  // Manejar el envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Crear objeto con los filtros
    const filtros = {
      fechaInicio: format(fechaInicio, 'yyyy-MM-dd'),
      fechaFin: format(fechaFin, 'yyyy-MM-dd')
    };
    
    // Solo agregar los filtros con valores
    if (ejecutivoId) {
      filtros.ejecutivo = ejecutivoId;
    }
    
    if (medioId) {
      filtros.medio = medioId;
    }
    
    if (empresaClienteId || empresaClienteNombre) {
      filtros.empresaCliente = empresaClienteId || empresaClienteNombre;
    }
    
    if (estadoAvisoId) {
      filtros.estadoAviso = estadoAvisoId;
    }
    
    console.log('Filtros aplicados:', filtros);
    
    // Enviar filtros al componente padre
    onFilter(filtros);
  };
  
  return (
    <div className="filtros-container mb-4 p-3 border rounded bg-light">
      <h4 className="mb-3">Reporte avisos</h4>
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={6} lg={2}>
            <Form.Group className="mb-3">
              <Form.Label>Fecha de publicación</Form.Label>
              <DatePicker
                selected={fechaInicio}
                onChange={date => setFechaInicio(date)}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                locale={es}
              />
            </Form.Group>
          </Col>
          <Col md={6} lg={2}>
            <Form.Group className="mb-3">
              <Form.Label>Fecha final</Form.Label>
              <DatePicker
                selected={fechaFin}
                onChange={date => setFechaFin(date)}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                locale={es}
                minDate={fechaInicio}
              />
            </Form.Group>
          </Col>
          <Col md={6} lg={2}>
            <Form.Group className="mb-3 position-relative" ref={ejecutivoRef}>
              <Form.Label>Ejecutivo</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar ejecutivo..."
                value={ejecutivoNombre}
                onChange={(e) => filtrarEjecutivos(e.target.value)}
                onFocus={() => {
                  if (ejecutivosFiltrados.length > 0) {
                    setMostrarSugerenciasEjecutivo(true);
                  }
                }}
              />
              {mostrarSugerenciasEjecutivo && ejecutivosFiltrados.length > 0 && (
                <ListGroup className="position-absolute w-100 mt-1 z-index-1000" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                  <ListGroup.Item 
                    action
                    onClick={() => seleccionarEjecutivo('', '')}
                    className="py-2 bg-light"
                  >
                    <strong>Todos</strong>
                  </ListGroup.Item>
                  {ejecutivosFiltrados.map(ejecutivo => (
                    <ListGroup.Item 
                      key={ejecutivo.id}
                      action
                      onClick={() => seleccionarEjecutivo(ejecutivo.id, ejecutivo.nombre)}
                      className="py-2"
                    >
                      {ejecutivo.nombre}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Form.Group>
          </Col>
          <Col md={6} lg={2}>
            <Form.Group className="mb-3 position-relative" ref={medioRef}>
              <Form.Label>Medio</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar medio..."
                value={medioNombre}
                onChange={(e) => filtrarMedios(e.target.value)}
                onFocus={() => {
                  if (mediosFiltrados.length > 0) {
                    setMostrarSugerenciasMedio(true);
                  }
                }}
              />
              {mostrarSugerenciasMedio && mediosFiltrados.length > 0 && (
                <ListGroup className="position-absolute w-100 mt-1 z-index-1000" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                  <ListGroup.Item 
                    action
                    onClick={() => seleccionarMedio('', '')}
                    className="py-2 bg-light"
                  >
                    <strong>Todos</strong>
                  </ListGroup.Item>
                  {mediosFiltrados.map(medio => (
                    <ListGroup.Item 
                      key={medio.id}
                      action
                      onClick={() => seleccionarMedio(medio.id, medio.nombre)}
                      className="py-2"
                    >
                      {medio.nombre}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Form.Group>
          </Col>
          <Col md={6} lg={2}>
            <Form.Group className="mb-3">
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={estadoAvisoId}
                onChange={e => setEstadoAvisoId(e.target.value)}
              >
                <option value="">Todos</option>
                {estadosAviso.map(estado => (
                  <option key={estado.id} value={estado.id}>
                    {estado.nombre}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6} lg={2}>
            <Form.Group className="mb-3 position-relative" ref={empresaClienteRef}>
              <Form.Label>Empresa cliente</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar empresa..."
                value={empresaClienteNombre}
                onChange={(e) => buscarEmpresasClientes(e.target.value)}
                onFocus={() => {
                  if (empresasClientes.length > 0) {
                    setMostrarSugerencias(true);
                  }
                }}
              />
              {mostrarSugerencias && empresasClientes.length > 0 && (
                <ListGroup className="position-absolute w-100 mt-1 z-index-1000" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1000 }}>
                  {empresasClientes.map(empresa => (
                    <ListGroup.Item 
                      key={empresa.id}
                      action
                      onClick={() => seleccionarEmpresaCliente(empresa.id, empresa.nombre)}
                      className="py-2"
                    >
                      {empresa.nombre}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button variant="primary" type="submit" className="mb-3">
              Aplicar
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Filtros; 